import { isValidCanadianPostal, isValidEmail, isValidNanpPhone } from '../intake/validators'

export const COMMERCIAL_SERVICES = [
  { value: 'solar', label: 'Solar / heat and glare' },
  { value: 'privacy', label: 'Privacy / frosting' },
  { value: 'security', label: 'Security / glass protection' },
] as const
export const COMMERCIAL_PROPERTIES = [
  { value: 'office', label: 'Office / professional space' },
  { value: 'retail', label: 'Retail / showroom / dealership' },
  { value: 'hospitality', label: 'Restaurant / hospitality' },
  { value: 'managed-property', label: 'Managed building / portfolio' },
  { value: 'construction', label: 'Construction / refurbishment' },
  { value: 'other', label: 'Other commercial property' },
] as const
export const COMMERCIAL_SCOPES = [
  { value: 'several-spaces', label: 'Several windows or rooms' },
  { value: 'whole-floor', label: 'Whole floor / business premises' },
  { value: 'whole-building', label: 'Whole building / extensive glazing' },
  { value: 'multiple-locations', label: 'Multiple locations' },
  { value: 'unsure', label: 'Not sure yet' },
] as const
export type EnquiryAudience = 'commercial' | 'all'
export const FILM_PROPERTIES = [
  { value: 'home', label: 'House / townhouse' },
  { value: 'condo', label: 'Condo / apartment' },
  ...COMMERCIAL_PROPERTIES,
] as const
export const FILM_SCOPES = [
  { value: 'single-window', label: 'One window or glass door' },
  { value: 'whole-home', label: 'Whole home / condo' },
  ...COMMERCIAL_SCOPES,
] as const
const isResidential = (f: CommercialEnquiry, audience: EnquiryAudience) => audience === 'all' && ['home', 'condo'].includes(f.propertyType)
export type CommercialEnquiry = {
  name: string; phone: string; email: string; company: string; city: string; postal: string
  propertyType: string; services: string[]; scope: string; notes: string
}
const selectionLabel = (options: readonly { value: string; label: string }[], value: string) => options.find(o => o.value === value)?.label ?? ''
export function validateCommercialEnquiry(f: CommercialEnquiry, audience: EnquiryAudience = 'commercial'): { field: keyof CommercialEnquiry; message: string } | null {
  if (!f.name.trim() || f.name.length > 200) return { field: 'name', message: 'Please enter your name.' }
  if (!isValidNanpPhone(f.phone)) return { field: 'phone', message: 'Please enter a 10-digit phone number.' }
  if (f.email && !isValidEmail(f.email)) return { field: 'email', message: 'Please enter a valid email address or leave it blank.' }
  if ((!isResidential(f, audience) && !f.company.trim()) || f.company.length > 100) return { field: 'company', message: 'Please enter the business or organization name.' }
  if (!f.city.trim() || f.city.length > 100) return { field: 'city', message: 'Please enter the project city.' }
  if (!isValidCanadianPostal(f.postal)) return { field: 'postal', message: 'Please enter a Canadian postal code.' }
  if (!selectionLabel(audience === 'all' ? FILM_PROPERTIES : COMMERCIAL_PROPERTIES, f.propertyType)) return { field: 'propertyType', message: 'Please choose the property type.' }
  if (!f.services.length || f.services.some(s => !selectionLabel(COMMERCIAL_SERVICES, s))) return { field: 'services', message: 'Please select at least one service.' }
  if (!selectionLabel(audience === 'all' ? FILM_SCOPES : COMMERCIAL_SCOPES, f.scope)) return { field: 'scope', message: 'Please choose a project size, or select not sure yet.' }
  if (f.notes.length > 400) return { field: 'notes', message: 'Please keep the project description within 400 characters.' }
  return null
}
export function buildCommercialEnquiry(f: CommercialEnquiry, request: { submissionId: string; mountedAt: number; turnstileToken: string }, audience: EnquiryAudience = 'commercial') {
  return {
    intent: 'callback_lead' as const,
    submissionId: request.submissionId,
    turnstileToken: request.turnstileToken,
    formFields: {
      name: f.name.trim(), phone: f.phone.trim(), email: f.email.trim(), postal: f.postal.trim(), city: f.city.trim(),
      concerns: [...(isResidential(f, audience) ? [] : ['commercial']), ...f.services.map(s => selectionLabel(COMMERCIAL_SERVICES, s))],
      notes: [
        audience === 'all' ? 'WINDOW FILM QUOTE REQUEST' : 'COMMERCIAL QUOTE REQUEST',
        'Please qualify by phone before arranging a site visit.',
        f.company.trim() ? `Organization: ${f.company.trim()}` : '',
        `Project city: ${f.city.trim()}`,
        `Property: ${selectionLabel(audience === 'all' ? FILM_PROPERTIES : COMMERCIAL_PROPERTIES, f.propertyType)}`,
        `Services: ${f.services.map(s => selectionLabel(COMMERCIAL_SERVICES, s)).join(', ')}`,
        `Scope: ${selectionLabel(audience === 'all' ? FILM_SCOPES : COMMERCIAL_SCOPES, f.scope)}`,
        f.notes.trim(),
      ].filter(Boolean).join('\n'),
      mountedAt: request.mountedAt,
    },
  }
}
